import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { YStack, XStack, Text, Spinner } from "tamagui";
import { colors } from "@styles/colors";
import appToast from "@components/Toast";
import { trackGa } from "@lib/tracker";
import { captureSmsInviteFromUrl } from "@utils/smsInvite";
import { reportSmsOpened } from "@utils/smsOpened";
import { SmsInviteOpenParams, buildSmsInviteBrowserPath } from "@utils/smsInviteOpen";
import { isValidShortToken, resolveShortLink, toRelayParams } from "@utils/smsShortLink";
import SmsInviteRelayView from "@screen/SmsInviteOpen/RelayView";

/** token 查無效 / 格式錯時的錯誤提示；文案與 /sms-invite/open 系列保持一致的語氣。 */
const INVALID_MESSAGE = "連結已失效，請向瓦斯行索取新的連結";
/** 與 /sms-invite/open 沒帶有效參數時相同的 fallback：交給 /sms-invite 落地頁自己導去序號註冊保底。 */
const FALLBACK_PATH = "/sms-invite";

type Status = "loading" | "network_error" | "ready";

/**
 * 簡訊邀請短連結中繼頁 `/s/{token}`。
 *
 * 簡訊按字數計費，長邀請連結（/sms-invite/open?liffId=…&phone=…&invite=…&order_id=…）
 * 換成短碼 `/s/{token}`，實際參數改存後端（lpg-line-invite-sms-sqs）。本頁用 token
 * 向後端換回參數，換到之後畫面與行為跟 /sms-invite/open 完全同一份（RelayView）：
 *   - 有瓦斯行 LIFF ID → 自動嘗試開 LINE，並留「在 LINE 中開啟」按鈕與「在瀏覽器繼續」
 *     （手機系統常不會因程式導向就把 LINE 叫起來，只做自動導向客戶會卡在轉圈）
 *   - 沒有 → 導到本站 /sms-invite（退回預設 LIFF）
 *   - token 格式錯 / 後端查無此 token（過期、被清、格式錯）→ 提示「連結已失效」，
 *     導向與 /sms-invite/open 相同的錯誤 fallback（/sms-invite，由該頁保底導序號註冊頁）
 *   - 純網路問題（逾時、斷線、後端 5xx）→ 顯示重試按鈕，不把客戶直接彈走
 *
 * 本頁在 LineLoginProvider 內是旁路（isSmsShortLinkMode）：不初始化 LIFF、不導登入。
 */
const SmsShortLink = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>("loading");
  const [relayParams, setRelayParams] = useState<SmsInviteOpenParams | null>(null);
  // 避免 StrictMode 二次 render 或 retry 交錯造成重複請求
  const inFlightRef = useRef(false);

  const attemptResolve = () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus("loading");

    const t = (token ?? "").trim();
    if (!t || !isValidShortToken(t)) {
      trackGa("funnel_step", { funnel: "sms_invite", step: "short_link", result: "invalid_token" });
      appToast.error(INVALID_MESSAGE);
      window.location.replace(FALLBACK_PATH);
      return;
    }

    void resolveShortLink(t).then((result) => {
      if (!result.ok) {
        if (result.reason === "network") {
          // 逾時 / 斷線 / 後端 5xx：可能只是暫時的，給重試按鈕而不是直接把客戶彈走
          trackGa("funnel_step", { funnel: "sms_invite", step: "short_link", result: "network_error" });
          inFlightRef.current = false;
          setStatus("network_error");
          return;
        }
        // not_found（token 不存在/過期/格式錯）或 config（環境變數沒設）：
        // 都沒有東西可以換，比照 /sms-invite/open 沒有有效參數時的錯誤 fallback
        trackGa("funnel_step", { funnel: "sms_invite", step: "short_link", result: result.reason });
        appToast.error(INVALID_MESSAGE);
        window.location.replace(FALLBACK_PATH);
        return;
      }

      const resolved = toRelayParams(result.data);
      const browserPath = buildSmsInviteBrowserPath(resolved);

      // 與 /sms-invite/open 相同的安全網：先把換回來的參數存進 sessionStorage，
      // 之後不論走哪條路都拿得到（見 smsInvite.ts；只取 browserPath 的 query 部分餵它）
      const queryIndex = browserPath.indexOf("?");
      captureSmsInviteFromUrl(queryIndex >= 0 ? browserPath.slice(queryIndex) : "");
      // 進站就回報開啟（不等 LINE 登入；失敗忽略），與 /sms-invite/open 一致
      void reportSmsOpened({
        invite: resolved.invite,
        order_id: resolved.order_id,
        phone: resolved.phone,
        k: resolved.k,
      });

      trackGa("funnel_step", { funnel: "sms_invite", step: "short_link", result: "resolved" });
      // 交給共用的中繼畫面：自動嘗試開 LINE＋手動按鈕＋瀏覽器退路
      inFlightRef.current = false;
      setRelayParams(resolved);
      setStatus("ready");
    });
  };

  useEffect(() => {
    attemptResolve();
    // 本專案未啟用 react-hooks/exhaustive-deps（.eslintrc.js 關閉），attemptResolve 不必進 deps
  }, [token]);

  const handleRetry = () => {
    trackGa("funnel_step", { funnel: "sms_invite", step: "short_link", result: "retry_tap" });
    attemptResolve();
  };

  if (status === "ready" && relayParams) {
    return <SmsInviteRelayView params={relayParams} />;
  }

  if (status === "network_error") {
    return (
      <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} px="$6" gap="$5">
        <Text fontSize={14} color={colors.gray} textAlign="center" lineHeight={22}>
          連線有點不穩，請重新整理再試一次
        </Text>
        <XStack
          ai="center"
          jc="center"
          bg={colors.primary}
          br={12}
          py="$3"
          px="$6"
          pressStyle={{ opacity: 0.85 }}
          onPress={handleRetry}
        >
          <Text fontSize={16} fontWeight="700" color={colors.light}>
            重新整理
          </Text>
        </XStack>
      </YStack>
    );
  }

  // loading（含導頁前的極短暫渲染）：與 /sms-invite 落地頁相同的 loading 畫面
  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} gap="$4">
      <Spinner size="large" color={colors.primary} />
    </YStack>
  );
};

export default SmsShortLink;
